import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { cadeConsistencyParams, fetchCadeConsistency } from '@/services/cade';
import { Stat } from '@/pages/cade/Stat';
import type {
  CadeAdjudicationReach,
  CadeConsistencyFunnel,
  CadeConsistencyPair,
  CadeConsistencyQuery,
} from '@/services/types';

const PAGE_SIZE = 25;

/**
 * CADE's consistency review: a queue of candidates, and the verdict layer that
 * found nothing.
 *
 * Two things share this screen and must not be read as one. The QUEUE is
 * comparator candidates -- decisions citing the same provisions that reached
 * different outcomes. Nothing in it has been adjudicated, so a differing outcome
 * is a reason to look and not a finding.
 *
 * The ADJUDICATION panel is what keeps that honest. CADE's contradiction model
 * returned zero flagged pairs from 1,547 verdicts, which reads as "CADE never
 * contradicts itself" unless the coverage is stated beside it: the model
 * compares extracted legal propositions, and those exist for a twentieth of the
 * corpus. It is given its own panel rather than a footnote for that reason.
 */
export function CadeConsistencyPage() {
  const { t } = useTranslation(['cade', 'common']);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState<CadeConsistencyQuery>({});

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'consistency', filters, offset],
    queryFn: () => fetchCadeConsistency({ ...filters, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  const narrow = (next: Partial<CadeConsistencyQuery>) => {
    setFilters((current) => ({ ...current, ...next }));
    setOffset(0);
  };

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:consistency.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:consistency.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cade:consistency.explainer" />

      <AsOfStrip asOf={data.as_of} />

      {/* Leads the page: everything below is a candidate, and a reader who
          misses that will read the queue as a list of findings. */}
      <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground">
        <AlertTriangle
          className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden
        />
        {t('cade:consistency.notAFinding')}
      </p>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:consistency.funnel.title')}
        </h2>
        <Answer answer={data.queue} help={t('cade:consistency.funnel.help')}>
          {(funnel) => <FunnelPanel funnel={funnel} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:consistency.guide.funnel" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('cade:consistency.adjudication.title')}
        </h2>
        <Answer answer={data.adjudication} help={t('cade:consistency.adjudication.help')}>
          {(reach) => <AdjudicationPanel reach={reach} />}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="cade:consistency.guide.adjudication" />
        </div>
      </section>

      {/* Queue narrowing, below the panels that define the population. Options
          come from the surviving pairs themselves: a type or citation no queued
          pair carries would only ever return empty. */}
      {data ? (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
          <div className="space-y-1">
            <label htmlFor="cade-consistency-type" className="block text-xs font-medium text-foreground">
              {t('cade:consistency.filters.caseType')}
            </label>
            <select
              id="cade-consistency-type"
              value={filters.caseType ?? ''}
              onChange={(event) => narrow({ caseType: event.target.value || null })}
              className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
            >
              <option value="">{t('cade:consistency.filters.caseTypeAny')}</option>
              {data.case_types.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="cade-consistency-citation" className="block text-xs font-medium text-foreground">
              {t('cade:consistency.filters.citation')}
            </label>
            <select
              id="cade-consistency-citation"
              value={filters.citation ?? ''}
              onChange={(event) => narrow({ citation: event.target.value || null })}
              className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
            >
              <option value="">{t('cade:consistency.filters.citationAny')}</option>
              {data.citations.map((citation) => (
                <option key={citation} value={citation}>
                  {citation}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : null}

      <div className="flex justify-end">
        <ExportButton
          path={`/api/v1/cade/export/consistency?${cadeConsistencyParams(filters)}`}
        />
      </div>

      <QueueList
        items={data.items}
        total={data.total}
        offset={offset}
        onPage={setOffset}
      />
    </div>
  );
}

/**
 * The funnel as a ladder, every stage visible.
 *
 * Any single number from it invites the wrong reading: 542 "divergent pairs"
 * sounds like a finding until you see that most of them pair different case
 * types or rest on an adjournment.
 */
function FunnelPanel({ funnel }: { funnel: CadeConsistencyFunnel }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  const stages: Array<{ key: string; value: number }> = [
    { key: 'allPairs', value: funnel.total_pairs },
    { key: 'outcomeKeyed', value: funnel.outcome_keyed_pairs },
    { key: 'citation', value: funnel.citation_pairs },
    { key: 'classified', value: funnel.both_classified },
    { key: 'divergent', value: funnel.divergent },
    { key: 'sameType', value: funnel.same_case_type },
    { key: 'substantive', value: funnel.substantive },
  ];

  return (
    <div className="space-y-3">
      <ol className="space-y-1 text-xs">
        {stages.map((stage) => (
          <li key={stage.key} className="flex items-baseline justify-between gap-3">
            <span
              className={
                stage.key === 'substantive' ? 'font-medium text-foreground' : 'text-muted-foreground'
              }
            >
              {t(`cade:consistency.funnel.${stage.key}`)}
            </span>
            <span
              className={`shrink-0 tabular-nums ${
                stage.key === 'substantive' ? 'font-semibold text-foreground' : 'text-muted-foreground'
              }`}
            >
              {number.format(stage.value)}
            </span>
          </li>
        ))}
      </ol>
      <p className="rounded border border-dashed bg-muted/30 p-2 text-xs text-muted-foreground">
        {t('cade:consistency.funnel.coverage', {
          decisions: number.format(funnel.decisions_covered),
          total: number.format(funnel.final_decisions),
          share: percent.format(funnel.decision_coverage),
        })}
      </p>
    </div>
  );
}

/**
 * The verdict layer, and the coverage that explains its zero.
 *
 * The two numbers are deliberately adjacent: 1,547 verdicts looks like a
 * thorough search, and only the proposition coverage beside it shows the model
 * was reading a twentieth of the corpus.
 */
function AdjudicationPanel({ reach }: { reach: CadeAdjudicationReach }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-3 text-xs">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label={t('cade:consistency.adjudication.verdicts')} value={number.format(reach.verdicts)} />
        <Stat
          label={t('cade:consistency.adjudication.flagged')}
          value={number.format(reach.contradictions)}
        />
        <Stat
          label={t('cade:consistency.adjudication.propositions')}
          value={number.format(reach.propositions)}
        />
        <Stat
          label={t('cade:consistency.adjudication.compared')}
          value={`${number.format(reach.compared)} / ${number.format(reach.verdicts)}`}
        />
        <Stat
          label={t('cade:consistency.adjudication.declined')}
          value={number.format(reach.insufficient_evidence)}
        />
        {/* The third verdict. Flagged + no-conflict + declined = verdicts, and
            the middle term was on the wire and never shown, so the three
            figures on screen did not add up to the first one. */}
        <Stat
          label={t('cade:consistency.adjudication.noConflict')}
          value={number.format(reach.no_contradiction)}
        />
        <Stat
          label={t('cade:consistency.adjudication.reasoningCoverage')}
          value={`${number.format(reach.proposition_decisions)} / ${number.format(
            reach.decisions,
          )}`}
          hint={percent.format(reach.proposition_coverage)}
        />
      </dl>

      {/* The per-model list (model · prompt · verdicts · declined share) was
          removed by request on 2026-09-14. The verdicts still carry their model
          and prompt on the wire and in the export's adjudication note; the
          screen shows the pooled reach only. */}
      {reach.found_nothing ? (
        <p className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/5 p-2 text-foreground">
          <AlertTriangle
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          <span>
            {t('cade:consistency.adjudication.foundNothing', {
              verdicts: number.format(reach.verdicts),
              compared: number.format(reach.compared),
              share: percent.format(reach.compared_share),
              declined: number.format(reach.insufficient_evidence),
            })}
          </span>
        </p>
      ) : null}
    </div>
  );
}


function QueueList({
  items,
  total,
  offset,
  onPage,
}: {
  items: CadeConsistencyPair[];
  total: number;
  offset: number;
  onPage: (next: number) => void;
}) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-3">
      {/* A filtered queue can be empty. The caption then read "1–0 de 0" over
          nothing, which is a caption and not a state. */}
      {items.length === 0 ? (
        <EmptyState hint={t('cade:consistency.emptyHint')} />
      ) : (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {t('cade:consistency.showing', {
            from: number.format(offset + 1),
            to: number.format(offset + items.length),
            total: number.format(total),
          })}
        </p>
      )}

      <ul className="space-y-3">
        {items.map((pair) => (
          <li key={pair.pair_key} className="rounded-lg border bg-card p-4">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2 text-xs">
              <span className="text-muted-foreground">{pair.case_type ?? '—'}</span>
              <span className="tabular-nums text-muted-foreground">
                {t('cade:consistency.shared', { count: pair.shared_count })}
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <PairSide
                caseNumber={pair.case_a}
                caseKey={pair.case_key_a}
                item={pair.item_a}
                outcomes={pair.outcomes_a}
                relator={pair.relator_a}
                relatorSlug={pair.relator_slug_a}
                sessionDate={pair.session_date_a}
                sessionYear={pair.session_year_a}
                fine={pair.fine_a}
              />
              <PairSide
                caseNumber={pair.case_b}
                caseKey={pair.case_key_b}
                item={pair.item_b}
                outcomes={pair.outcomes_b}
                relator={pair.relator_b}
                relatorSlug={pair.relator_slug_b}
                sessionDate={pair.session_date_b}
                sessionYear={pair.session_year_b}
                fine={pair.fine_b}
              />
            </div>

            {pair.shared_citations.length > 0 ? (
              <ul className="mt-2 flex flex-wrap gap-1">
                {pair.shared_citations.map((citation) => (
                  <li
                    key={citation}
                    className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                  >
                    {citation}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => onPage(Math.max(offset - PAGE_SIZE, 0))}
          className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {t('cade:consistency.previous')}
        </button>
        <button
          type="button"
          disabled={offset + PAGE_SIZE >= total}
          onClick={() => onPage(offset + PAGE_SIZE)}
          className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {t('cade:consistency.next')}
        </button>
      </div>
    </div>
  );
}

function PairSide({
  caseNumber,
  caseKey,
  item,
  outcomes,
  relator,
  relatorSlug,
  sessionDate,
  sessionYear,
  fine,
}: {
  caseNumber: string;
  caseKey: string;
  item: string | null;
  outcomes: string[];
  relator: string | null;
  relatorSlug: string | null;
  sessionDate: string | null;
  sessionYear: number | null;
  fine: number | null;
}) {
  const { t, i18n } = useTranslation(['cade']);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

  return (
    <div className="rounded border p-2.5 text-xs">
      <Link
        to={`/cade/c/${caseKey}`}
        className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
      >
        {caseNumber}
      </Link>
      {item ? <span className="ml-2 text-muted-foreground">{t('cade:case.item', { item })}</span> : null}

      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {(outcomes.length === 0 ? ['UNCLASSIFIED'] : outcomes).map((code) => (
          <span key={code} className="rounded border px-2 py-0.5 text-muted-foreground">
            {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
          </span>
        ))}
        {fine !== null && fine > 0 ? (
          <span className="rounded border border-primary/40 bg-primary/10 px-2 py-0.5 tabular-nums text-foreground">
            {currency.format(fine)}
          </span>
        ) : null}
      </div>

      <dl className="mt-1.5 space-y-0.5 text-muted-foreground">
        <div>
          <dt className="inline">{t('cade:case.relator')}: </dt>
          <dd className="inline text-foreground">
            {relator && relatorSlug ? (
              <Link to={`/cade/r/${relatorSlug}`} className="text-primary underline-offset-4 hover:underline">
                {relator}
              </Link>
            ) : (
              (relator ?? '—')
            )}
          </dd>
        </div>
        <div>
          <dt className="inline">{t('cade:case.comparables.session')}: </dt>
          {/* Year first: the two sides of a pair are read for how far apart
              they sat, and the prose date is missing on a quarter of the
              sessions while the folder year is on every one. */}
          <dd className="inline tabular-nums text-foreground">
            {[sessionYear, sessionDate].filter((part) => part !== null).join(' · ') || '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
}
